import { useMemo } from "react";

import { useBoostPackages } from "@/Apollo/hooks/boost/useBoostPackages";
import { ProductUnknown } from "@/common/contracts/product.contracts";
import { BoostPackage } from "@/features/create-ad-packages/contracts/create-ad-packages.contracts";

const NO_PACKAGES: BoostPackage[] = [];

/**
 * Loads boost packages and maps them into the packages view model. `discounted`
 * picks the create-ad discount price over the standard one — it must match the
 * `discount` flag sent to `addBoostToPackageCart`, or the shown price and the
 * charged price disagree. Offers nothing when the product can't be boosted or is
 * already boosted, so clients don't show a boost the merchant can't buy
 * (addBoostToPackageCart guards this server-side too).
 */
export const useCreateAdBoostPackages = (
  product?: ProductUnknown,
  discounted = false
) => {
  const { data, loading, error } = useBoostPackages();

  const boostable = !!product?.isBoostable && !product?.isBoosted;

  const packages = useMemo<BoostPackage[]>(() => {
    const options = data?.boostPackages;

    if (!boostable || !options?.length) {
      return NO_PACKAGES;
    }

    return options
      .map<BoostPackage>(option => {
        const charged = discounted
          ? option.discountedPrice
          : option.originalPrice;

        return {
          days: option.days,
          price: charged.amount,
          originalPrice: option.originalPrice.amount,
          currency: charged.currency,
          recommended: option.isMostPopular,
        };
      })
      .sort((a, b) => a.days - b.days);
  }, [boostable, data?.boostPackages, discounted]);

  return { packages, loading, error };
};
